import React, { FC, forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { IDataTableProps, IDataTableRef } from './models';
import { DataTableHeadRow } from './_Head/DataTableHeadRow';
import { DataTablePaging } from './_Paging/DataTablePaging';
import { Box } from '@src/atoms/Box';
import { Unit } from '@src/core/Unit';
import { Provider, useUnit } from 'effector-react';
import { $tableStore, setOnChangeCallbackEvent } from './store';
import { initStateEvent } from './store/events/initStateEvent';
import { $columnsStore, initColumnsEvent } from './store/columns';
import { initRowsEvent } from './store/events/initRowsEvent';
import { fork } from 'effector';
import { EmptyDataDefault } from './_EmptyData/EmptyData';
import { DataTableBodyRows } from './_BodyRow/DataTableBodyRow';
import { DATA_ELEM_BASE, DATA_ELEM_WRAPPER, DEFAULT_TABLE_SKELETON } from './consts';
import { DataTableSkeleton } from './_Skeleton/DataTableSkeleton';

import styled from './DataTable.module.scss';
import { classnames } from '@src/utils/classnames';
import { getClasses } from '@src/utils/getClass';
import { setDefaultPagingEvent } from '@src/molecules/DataTable/store/events/setDefaultPagingEvent';
import { DataTableContext } from './context';
import { $rowsStore } from './store/rows';

const DataTableComponent: FC<Omit<IDataTableProps, 'updateList'>> = ({
  initState,
  columns,
  rows,
  totalRows = 0,
  EmptyDataComponent = <EmptyDataDefault />,
  onChange = null,
  bodyRef,
  view = 'default',
  hidePaging: hidePagingProps = false,
  isLoading = false,
  LoadingSkeleton = <DataTableSkeleton view={view} {...DEFAULT_TABLE_SKELETON} />,
  rowIdField,
  pagingProps,
}) => {
  const [setOnChangeCallbackEventFn, initStateFn, initColumnsEventFn, initRowsEventFn, tableStore] = useUnit([
    setOnChangeCallbackEvent,
    initStateEvent,
    initColumnsEvent,
    initRowsEvent,
    $tableStore,
  ]);

  const isHasActions = rows.some((r) => !!r.actions);
  const isEmpty = totalRows === undefined || totalRows === 0;
  const rootClass = classnames(styled.root, ...getClasses(styled, { view }));

  useEffect(() => {
    setOnChangeCallbackEventFn(onChange);
  }, [onChange, setOnChangeCallbackEventFn]);

  useEffect(() => {
    initState && initStateFn(initState);
  }, [initState, initStateFn]);

  useEffect(() => {
    initColumnsEventFn({ data: columns, view: view, isHasActions });
  }, [columns, initColumnsEventFn, isHasActions, view]);

  useEffect(() => {
    initRowsEventFn(rows);
  }, [initRowsEventFn, rows]);

  const hidePaging = hidePagingProps || tableStore.pagingOptions[0].value >= totalRows;

  return (
    <Unit id={'DataTable'}>
      <Box className={rootClass} column fullWidth data-elem={DATA_ELEM_BASE}>
        <Box
          ref={bodyRef}
          fullWidth
          paddingBottom={hidePaging ? 16 : undefined}
          className={styled.wrapper}
          data-elem={DATA_ELEM_WRAPPER}
        >
          <div className={styled.table}>
            {isLoading ? (
              LoadingSkeleton
            ) : (
              <>
                <DataTableHeadRow view={view} />
                <DataTableBodyRows view={view} rowIdField={rowIdField} />
              </>
            )}
          </div>
        </Box>
        {isEmpty && !isLoading ? (
          EmptyDataComponent
        ) : (
          <DataTablePaging
            pagingProps={pagingProps}
            currentPageRows={rows?.length}
            totalRows={totalRows}
            hidePaging={hidePaging}
          />
        )}
      </Box>
    </Unit>
  );
};

export const DataTableMiddleWare = forwardRef<IDataTableRef, IDataTableProps>(
  ({ updateList, internalData, ...props }, ref) => {
    const [setDefaultOffset, rows = [], columns = []] = useUnit([setDefaultPagingEvent, $rowsStore, $columnsStore]);

    const api = useRef<IDataTableRef>({
      setDefaultOffset,
      updateList,
      rows,
      columns,
      internalData,
    });

    /** Этот эффект нужен для синхронизации внешних пропсов, и внутренних состояний в api */
    useEffect(() => {
      api.current.updateList = updateList;
      api.current.columns = columns;
      api.current.rows = rows;
      api.current.internalData = internalData;
    }, [columns, internalData, rows, updateList]);

    useImperativeHandle(
      ref,
      () => {
        return api.current;
      },
      [],
    );

    const value = useMemo(() => ({ api: api.current }), []);

    return (
      <DataTableContext.Provider value={value}>
        <DataTableComponent {...props} />
      </DataTableContext.Provider>
    );
  },
);

/** Для правильной изоляции эффектора и react контекста используем `DataTableMiddleWare` */
export const DataTable = forwardRef<IDataTableRef, IDataTableProps>((props, ref) => {
  const scope = useMemo(() => fork(), []);

  return (
    <Provider value={scope}>
      <DataTableMiddleWare {...props} ref={ref} />
    </Provider>
  );
});
